import React, { useState, useRef, useEffect, useCallback } from "react";
import { AgentIcon } from "./presence";
import VoiceInput from "./chat/VoiceInput";

interface InlineSendResult {
  assistantReply?: string;
  acknowledgment?: string;
  closeAfterSend?: boolean;
}

interface InlineTurn {
  id: string;
  role: "user" | "assistant";
  text: string;
}

interface InlineChatComposeProps {
  label: string;
  contextId: string;
  contextType: "life_item" | "presence" | "sidebar" | "file";
  onSend: (text: string) => void | InlineSendResult | Promise<void | InlineSendResult>;
  onClose: () => void;
}

/**
 * Small floating compose area that attaches below any element.
 * Generic, reusable across Life cards, sidebar items, file entries.
 * Supports contextual multi-turn dialogue directly in-pane.
 */
export default function InlineChatCompose({
  label,
  contextId,
  contextType,
  onSend,
  onClose,
}: InlineChatComposeProps): React.ReactElement {
  const [text, setText] = useState("");
  const [mounted, setMounted] = useState(false);
  const [turns, setTurns] = useState<InlineTurn[]>([]);
  const [sending, setSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
    textareaRef.current?.focus();
  }, []);

  // Auto-resize textarea to fit content
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  }, []);

  useEffect(() => {
    autoResize();
  }, [text, autoResize]);

  useEffect(() => {
    const thread = threadRef.current;
    if (!thread) return;
    thread.scrollTop = thread.scrollHeight;
  }, [turns, sending]);

  const handleSubmit = async () => {
    if (!text.trim() || sending) return;

    const userText = text.trim();
    setText("");
    setTurns((prev) => [
      ...prev,
      {
        id: `inline-user-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        role: "user",
        text: userText,
      },
    ]);

    setSending(true);
    try {
      const result = await onSend(userText);
      const assistantReply =
        result?.assistantReply?.trim() ||
        result?.acknowledgment?.trim() ||
        "Got it. Want me to refine anything else here?";
      const closeAfterSend = result?.closeAfterSend ?? false;

      setTurns((prev) => [
        ...prev,
        {
          id: `inline-assistant-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          role: "assistant",
          text: assistantReply,
        },
      ]);

      if (closeAfterSend) {
        setTimeout(() => onClose(), 1800);
      } else {
        textareaRef.current?.focus();
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "I hit an error handling that request.";
      setTurns((prev) => [
        ...prev,
        {
          id: `inline-assistant-error-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          role: "assistant",
          text: message,
        },
      ]);
      textareaRef.current?.focus();
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    // Enter sends, Shift+Enter adds newline
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleVoiceTranscript = useCallback((transcript: string) => {
    const next = String(transcript || "").trim();
    if (!next) return;
    setText((prev) => (prev.trim() ? `${prev.trim()} ${next}` : next));
    textareaRef.current?.focus();
  }, []);

  return (
    <div
      data-inline-context-id={contextId}
      data-inline-context-type={contextType}
      style={{
        marginTop: 4,
        borderRadius: 8,
        overflow: "hidden",
        backgroundColor: "var(--c-surface-2)",
        border: "1px solid var(--c-border-hover)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)",
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(-4px)",
        transition: "opacity 0.15s ease, transform 0.15s ease",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "6px 10px",
          borderBottom: "1px solid var(--c-border)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <AgentIcon size={10} />
          <span
            style={{
              fontSize: 10,
              fontWeight: 500,
              color: "var(--c-text-secondary)",
              letterSpacing: "0.01em",
            }}
          >
            Re: {label}
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            padding: 2,
            borderRadius: 4,
            border: "none",
            backgroundColor: "transparent",
            color: "var(--c-text-faint)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "var(--c-text-secondary)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "var(--c-text-faint)";
          }}
        >
          <svg
            width="8"
            height="8"
            viewBox="0 0 8 8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M6 2L2 6M2 2l4 4" />
          </svg>
        </button>
      </div>

      {/* Transcript */}
      {turns.length > 0 && (
        <div
          ref={threadRef}
          style={{
            maxHeight: 180,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 6,
            padding: "8px",
            borderBottom: "1px solid var(--c-border)",
            backgroundColor: "var(--c-surface)",
          }}
        >
          {turns.map((turn) => (
            <div
              key={turn.id}
              style={{
                alignSelf: turn.role === "user" ? "flex-end" : "flex-start",
                maxWidth: "92%",
                borderRadius: 8,
                padding: "6px 8px",
                fontSize: 11,
                lineHeight: 1.45,
                border:
                  turn.role === "user"
                    ? "1px solid var(--c-primary-subtle)"
                    : "1px solid var(--c-border)",
                color: turn.role === "user" ? "var(--c-text)" : "var(--c-text-secondary)",
                backgroundColor: turn.role === "user" ? "var(--c-surface-3)" : "var(--c-surface-2)",
                whiteSpace: "pre-wrap",
              }}
            >
              {turn.text}
            </div>
          ))}
          {sending && (
            <div
              style={{
                alignSelf: "flex-start",
                fontSize: 11,
                color: "var(--c-text-faint)",
                padding: "0 2px",
              }}
            >
              Agent is thinking...
            </div>
          )}
        </div>
      )}

      {/* Input area */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 6,
          padding: "6px 8px",
        }}
      >
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={turns.length > 0 ? "Continue..." : "Say something..."}
          rows={1}
          style={{
            flex: 1,
            padding: "6px 8px",
            borderRadius: 5,
            fontSize: 12,
            lineHeight: 1.5,
            outline: "none",
            backgroundColor: "var(--c-surface-3)",
            color: "var(--c-text)",
            border: "1px solid var(--c-border)",
            boxSizing: "border-box",
            resize: "none",
            overflow: "hidden",
            minHeight: 30,
            maxHeight: 120,
            fontFamily: "inherit",
          }}
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!text.trim() || sending}
          style={{
            flexShrink: 0,
            padding: "5px 10px",
            borderRadius: 5,
            fontSize: 10,
            fontWeight: 500,
            border: "none",
            cursor: text.trim() && !sending ? "pointer" : "default",
            backgroundColor:
              text.trim() && !sending ? "var(--c-primary-subtle)" : "var(--c-surface-3)",
            color: text.trim() && !sending ? "var(--c-primary)" : "var(--c-text-faint)",
            opacity: sending ? 0.8 : 1,
          }}
        >
          {sending ? "..." : "Send"}
        </button>
        <VoiceInput onTranscript={handleVoiceTranscript} disabled={sending} />
      </div>
    </div>
  );
}
